// src/theme.ts
import { alpha, createTheme } from '@mui/material/styles';

const PI = {
  ink: '#07111F',
  blue: '#2968FF',
  cyan: '#35B9E9',
  positive: '#168A5B',
  watch: '#C78118',
  risk: '#D94A4A',
  canvas: '#F4F7FA',
  surface: '#FFFFFF',
  subtle: '#EDF2F6',
  research: '#F8FAFC',
  textSecondary: '#596779',
  textMuted: '#8491A1',
  border: '#E2E8EF',
  borderStrong: '#CBD5DF',
} as const;

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: PI.blue, dark: '#1F55D8', light: '#6F94FF', contrastText: '#FFFFFF' },
    secondary: { main: PI.cyan, dark: '#1A8FB8', contrastText: PI.ink },
    info: { main: PI.cyan },
    success: { main: PI.positive },
    warning: { main: PI.watch },
    error: { main: PI.risk },
    background: { default: PI.canvas, paper: PI.surface },
    divider: PI.border,
    text: { primary: PI.ink, secondary: PI.textSecondary },
  },
  shape: { borderRadius: 7 },
  typography: {
    fontFamily:
      'Pretendard, Inter, system-ui, -apple-system, Segoe UI, Roboto, Apple SD Gothic Neo, Noto Sans KR, sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.04em' },
    h2: { fontWeight: 800, letterSpacing: '-0.038em' },
    h3: { fontWeight: 800, letterSpacing: '-0.035em' },
    h4: { fontWeight: 800, letterSpacing: '-0.03em' },
    h5: { fontWeight: 800, letterSpacing: '-0.025em' },
    h6: { fontWeight: 750, letterSpacing: '-0.02em' },
    body1: { letterSpacing: '-0.008em' },
    body2: { letterSpacing: '-0.006em' },
    button: { fontWeight: 700, letterSpacing: '-0.005em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: PI.canvas,
          color: PI.ink,
          fontVariantNumeric: 'tabular-nums lining-nums',
        },
        'button, input, textarea, select': { fontFamily: 'inherit' },
        '::selection': { backgroundColor: alpha(PI.blue, 0.16) },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: 'none' } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${PI.border}`,
          boxShadow: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: { size: 'small', disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: 34,
          textTransform: 'none',
          borderRadius: 7,
          fontWeight: 700,
          boxShadow: 'none',
        },
        containedPrimary: { boxShadow: 'none', '&:hover': { boxShadow: 'none' } },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 7 } },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 5, fontWeight: 700, minHeight: 24 },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 0',
          backgroundColor: PI.surface,
          border: `1px solid ${PI.border}`,
          boxShadow: 'none',
          '&:before': { display: 'none' },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: { borderRadius: 8, border: `1px solid ${PI.border}`, boxShadow: '0 14px 34px rgba(7,17,31,.10)' },
        option: { borderRadius: 4 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 7,
          backgroundColor: PI.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: PI.borderStrong },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#AEBAC8' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PI.blue, borderWidth: 1 },
        },
      },
    },
    MuiTextField: { defaultProps: { size: 'small', variant: 'outlined' } },
    MuiTooltip: { defaultProps: { arrow: true } },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 12 } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 8, boxShadow: '0 14px 34px rgba(7,17,31,.12)' } },
    },
    MuiSnackbar: { styleOverrides: { root: { '& .MuiPaper-root': { borderRadius: 8 } } } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 10px',
          borderBottomColor: PI.border,
          fontVariantNumeric: 'tabular-nums lining-nums',
        },
        head: {
          color: PI.textSecondary,
          fontSize: 10.5,
          fontWeight: 800,
          backgroundColor: PI.subtle,
        },
      },
    },
    MuiTabs: { styleOverrides: { indicator: { height: 2, backgroundColor: PI.blue } } },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 38,
          textTransform: 'none',
          fontWeight: 700,
          color: PI.textSecondary,
          '&.Mui-selected': { color: PI.ink },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 7 } },
    },
  },
});

export default theme;
